import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { MealSession, VenueArea } from '@/types';

const sessionSchema = z.object({
  name: z.string().min(1, 'Session name is required'),
  defaultDurationMinutes: z.coerce.number().min(15, 'Duration must be at least 15 minutes'),
  gapBetweenBookingsMinutes: z.coerce.number().min(0, 'Gap cannot be negative'),
  minPartySize: z.coerce.number().min(1, 'Minimum party size must be at least 1'),
  maxPartySize: z.coerce.number().optional(),
  bookingLeadTimeHours: z.coerce.number().min(0, 'Lead time cannot be negative'),
  cancellationDeadlineHours: z.coerce.number().min(0, 'Cancellation deadline cannot be negative'),
  isActive: z.boolean().default(true),
});

type SessionFormData = z.infer<typeof sessionSchema>;

export default function SessionForm() {
  const { venueId, areaId, sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(sessionId);

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: area } = useQuery<VenueArea>({
    queryKey: ['venue-area', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}`);
      return response.data;
    },
  });

  const { data: session, isLoading: sessionLoading } = useQuery<MealSession>({
    queryKey: ['area-session', venueId, areaId, sessionId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      name: '',
      defaultDurationMinutes: 90,
      gapBetweenBookingsMinutes: 15,
      minPartySize: 1,
      maxPartySize: undefined,
      bookingLeadTimeHours: 24,
      cancellationDeadlineHours: 24,
      isActive: true,
    },
  });

  useEffect(() => {
    if (session) {
      reset({
        name: session.name,
        defaultDurationMinutes: session.default_duration_minutes,
        gapBetweenBookingsMinutes: session.gap_between_bookings_minutes,
        minPartySize: session.min_party_size,
        maxPartySize: session.max_party_size || undefined,
        bookingLeadTimeHours: session.booking_lead_time_hours,
        cancellationDeadlineHours: session.cancellation_deadline_hours,
        isActive: session.is_active,
      });
    }
  }, [session, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: SessionFormData) => {
      const response = await api.post(`/admin/venues/${venueId}/areas/${areaId}/sessions`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['area-sessions', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: SessionFormData) => {
      await api.put(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['area-sessions', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['area-session', venueId, areaId, sessionId] });
      navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`);
    },
  });

  const onSubmit = async (data: SessionFormData) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  if (isEditing && sessionLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Session' : 'Create Session'}
          </h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - {area?.name}
          </p>
        </div>
      </div>

      <div className="card max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {(createMutation.error as Error)?.message || (updateMutation.error as Error)?.message || 'An error occurred'}
            </div>
          )}

          <div>
            <label htmlFor="name" className="label">
              Session Name
            </label>
            <input
              id="name"
              type="text"
              className="input"
              placeholder="e.g., Breakfast, Lunch, Dinner, Afternoon Tea"
              {...register('name')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="defaultDurationMinutes" className="label">
                Default Duration (minutes)
              </label>
              <input
                id="defaultDurationMinutes"
                type="number"
                className="input"
                placeholder="90"
                {...register('defaultDurationMinutes')}
              />
              {errors.defaultDurationMinutes && (
                <p className="mt-1 text-sm text-red-600">{errors.defaultDurationMinutes.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">How long each booking lasts</p>
            </div>

            <div>
              <label htmlFor="gapBetweenBookingsMinutes" className="label">
                Gap Between Bookings (minutes)
              </label>
              <input
                id="gapBetweenBookingsMinutes"
                type="number"
                className="input"
                placeholder="15"
                {...register('gapBetweenBookingsMinutes')}
              />
              {errors.gapBetweenBookingsMinutes && (
                <p className="mt-1 text-sm text-red-600">{errors.gapBetweenBookingsMinutes.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Buffer time for table turnover</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="minPartySize" className="label">
                Minimum Party Size
              </label>
              <input
                id="minPartySize"
                type="number"
                className="input"
                placeholder="1"
                {...register('minPartySize')}
              />
              {errors.minPartySize && (
                <p className="mt-1 text-sm text-red-600">{errors.minPartySize.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="maxPartySize" className="label">
                Maximum Party Size (optional)
              </label>
              <input
                id="maxPartySize"
                type="number"
                className="input"
                placeholder="Leave blank for unlimited"
                {...register('maxPartySize')}
              />
              {errors.maxPartySize && (
                <p className="mt-1 text-sm text-red-600">{errors.maxPartySize.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="bookingLeadTimeHours" className="label">
                Booking Lead Time (hours)
              </label>
              <input
                id="bookingLeadTimeHours"
                type="number"
                className="input"
                placeholder="24"
                {...register('bookingLeadTimeHours')}
              />
              {errors.bookingLeadTimeHours && (
                <p className="mt-1 text-sm text-red-600">{errors.bookingLeadTimeHours.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">How far in advance bookings must be made</p>
            </div>

            <div>
              <label htmlFor="cancellationDeadlineHours" className="label">
                Cancellation Deadline (hours)
              </label>
              <input
                id="cancellationDeadlineHours"
                type="number"
                className="input"
                placeholder="24"
                {...register('cancellationDeadlineHours')}
              />
              {errors.cancellationDeadlineHours && (
                <p className="mt-1 text-sm text-red-600">{errors.cancellationDeadlineHours.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Guests must cancel this many hours before</p>
            </div>
          </div>

          {isEditing && (
            <div className="flex items-center gap-3">
              <input
                id="isActive"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('isActive')}
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Session is active and available for bookings
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Session' : 'Create Session'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
